import { useCallback, useMemo, useState } from 'react';
import { useDeploymentVariables } from '../api/queries';
import type { BlueprintDocument, Deployment, ResourceType } from '../api/types';
import type { DiagnosticIndex } from '../lib/diagnostics';
import {
  appSummary,
  deploymentRows,
  designRows,
  issueCounts,
  rowStatuses,
  withOpenDocument,
  type AppSummary,
  type RowStatus,
  type StatusContext,
  type TriggerDescriber,
  type ValueContext,
  type VariableApp,
  type VariableRow,
} from './model';

/** The window's scope (spec 6.10.2): the apps shown, and whether the designer's selection may change them. */
export interface VariablesScope {
  pinned: boolean;
  /** Resource IDs; null = every app. */
  ids: string[] | null;
}

export const ALL_APPS: VariablesScope = { pinned: false, ids: null };

/**
 * The page's scope state. In the designer an unpinned scope follows the canvas selection (`follow`); on other
 * pages the scope is what the user (or a link) chose.
 */
export function useVariablesScope(
  follow: string[] | null,
  followsSelection: boolean,
  initial: VariablesScope = ALL_APPS,
) {
  const [scope, setScope] = useState<VariablesScope>(initial);
  const effective = followsSelection && !scope.pinned ? follow : scope.ids;
  return { scope, setScope, effective };
}

export interface VariablesData {
  mode: 'design' | 'deployment';
  deployment: Deployment | null;
  loading: boolean;
  fetching: boolean;
  error: unknown;
  variables: ReturnType<typeof useDeploymentVariables>['data'];
  apps: VariableApp[];
  rows: VariableRow[];
  statuses: ReadonlyMap<string, RowStatus[]>;
  valueContext: ValueContext;
  statusContext: (row: VariableRow) => StatusContext;
  /** Footers and the inspector card. */
  summary: (resourceId: string) => AppSummary | null;
  /** Rows of the scope with a danger (errors) and a warning (warnings) worst status. */
  badge: { errors: number; warnings: number };
  scopeIds: string[] | null;
}

/**
 * The rows of the Variables window for a page (spec 6.10.2): design rows from the open document, or the
 * deployment's rows from `GET /deployments/{id}/settings` (with the open document's unsaved changes in the
 * designer). Shared by the window, the node footers, the inspector card, the resources table and the badge.
 */
export function useVariablesData({
  deployment,
  doc,
  savedDoc,
  types,
  diagnostics,
  scopeIds,
  describeTrigger,
}: {
  deployment: Deployment | null;
  doc?: BlueprintDocument | null;
  savedDoc?: BlueprintDocument | null;
  types: ReadonlyMap<string, ResourceType>;
  diagnostics?: DiagnosticIndex;
  scopeIds: string[] | null;
  /** Design-scope trigger rows from the provider descriptions (feature spec 16, 9.7). */
  describeTrigger?: TriggerDescriber;
}): VariablesData {
  const query = useDeploymentVariables(deployment?.id ?? null);
  const variables = query.data;
  const design = useMemo(
    () => (doc ? designRows(doc, types, diagnostics, describeTrigger) : null),
    [doc, types, diagnostics, describeTrigger],
  );
  const saved = useMemo(
    () =>
      savedDoc && doc && savedDoc !== doc ? designRows(savedDoc, types, undefined, describeTrigger) : null,
    [savedDoc, doc, types, describeTrigger],
  );
  const combined = useMemo(() => {
    if (!deployment) return design ?? { apps: [], rows: [] };
    if (!variables) return { apps: [], rows: [] };
    const fromServer = deploymentRows(variables);
    return design ? withOpenDocument(fromServer, design, saved) : fromServer;
  }, [deployment, design, saved, variables]);

  const listingFailed = !!variables?.listingError;
  const appsById = useMemo(() => new Map(combined.apps.map((a) => [a.resourceId, a])), [combined.apps]);
  const lastApplyAt = deployment?.lastSuccessfulApply?.completedAt ?? null;
  // Feature spec 15: per-app statuses: the apps rolling out and when each app read its secrets.
  const rollingOut = useMemo(
    () => new Set(combined.apps.filter(isRollingOut).map((a) => a.resourceId)),
    [combined.apps],
  );
  const appReadAt = useMemo(
    () => new Map(combined.apps.map((a) => [a.resourceId, a.appReferenceStatus?.readAt ?? null])),
    [combined.apps],
  );
  const statusContext = useMemo(
    () =>
      (row: VariableRow): StatusContext => ({
        listingFailed,
        referenceStatusCheckedAt: appsById.get(row.resourceId)?.referenceStatusCheckedAt ?? null,
        settingsDriftCheckedAt: appsById.get(row.resourceId)?.settingsDrift?.checkedAt ?? null,
        lastApplyAt,
        rollingOut,
        appReadAt,
      }),
    [listingFailed, appsById, lastApplyAt, rollingOut, appReadAt],
  );
  const statuses = useMemo(
    () => new Map(combined.rows.map((row) => [row.id, rowStatuses(row, statusContext(row))])),
    [combined.rows, statusContext],
  );
  const valueContext = useMemo<ValueContext>(
    () => ({
      listingFailed,
      appliedVersion: variables?.appliedVersion ?? null,
      lastApplyAt: deployment?.lastSuccessfulApply?.completedAt ?? null,
      deploymentName: deployment?.name,
    }),
    [
      listingFailed,
      variables?.appliedVersion,
      deployment?.lastSuccessfulApply?.completedAt,
      deployment?.name,
    ],
  );
  const scopeKey = scopeIds?.join('\n') ?? null;
  const badge = useMemo(() => {
    const ids = scopeKey === null ? null : new Set(scopeKey.split('\n'));
    const counts = issueCounts(
      combined.rows.filter((r) => !ids || ids.has(r.resourceId)).map((r) => statuses.get(r.id) ?? []),
    );
    // Feature spec 15: a danger or warning app status counts once for the app, unless a secret's rows carry it.
    for (const app of combined.apps) {
      if (ids && !ids.has(app.resourceId)) continue;
      const tone = appIssueTone(app, combined.rows);
      if (tone === 'danger') counts.errors++;
      else if (tone === 'warning') counts.warnings++;
    }
    return counts;
  }, [combined.rows, combined.apps, statuses, scopeKey]);
  const summaries = useMemo(
    () =>
      new Map(
        combined.apps.map((app) => [
          app.resourceId,
          withAppStatus(
            appSummary(
              combined.rows.filter((r) => r.resourceId === app.resourceId),
              {
                listingFailed,
                referenceStatusCheckedAt: app.referenceStatusCheckedAt,
                settingsDriftCheckedAt: app.settingsDrift?.checkedAt ?? null,
                lastApplyAt,
              },
            ),
            app,
            combined.rows,
          ),
        ]),
      ),
    [combined, listingFailed, lastApplyAt],
  );
  const summary = useCallback((resourceId: string) => summaries.get(resourceId) ?? null, [summaries]);

  return {
    mode: deployment ? 'deployment' : 'design',
    deployment,
    loading: !!deployment && query.isPending,
    fetching: query.isFetching,
    error: query.error,
    variables,
    apps: combined.apps,
    rows: combined.rows,
    statuses,
    valueContext,
    statusContext,
    summary,
    badge,
    scopeIds,
  };
}

/** Feature spec 15: an app status `pending` after a rollout through Nebisys. */
export function isRollingOut(app: Pick<VariableApp, 'appReferenceStatus' | 'lastRestart'>): boolean {
  return app.appReferenceStatus?.neutral === 'pending' && !!app.lastRestart;
}

/** The tone of an app-level reference status that counts as an issue of the app (19.3 Counting); null otherwise. */
/** Adds the app's own status (badge) and counts its issue once (feature spec 15 19.3 Counting). */
function withAppStatus(summary: AppSummary, app: VariableApp, rows: readonly VariableRow[]): AppSummary {
  if (!app.appReferenceStatus) return summary;
  const tone = appIssueTone(app, rows);
  const delivery =
    rows.find(
      (r) => r.resourceId === app.resourceId && r.delivery?.mode === 'reference' && r.delivery.nowAction,
    )?.delivery ?? null;
  return {
    ...summary,
    errors: summary.errors + (tone === 'danger' ? 1 : 0),
    warnings: summary.warnings + (tone === 'warning' ? 1 : 0),
    app,
    delivery,
  };
}

export function appIssueTone(
  app: Pick<VariableApp, 'resourceId' | 'appReferenceStatus'>,
  rows: readonly Pick<VariableRow, 'resourceId' | 'referenceStatus'>[],
): 'danger' | 'warning' | null {
  const neutral = app.appReferenceStatus?.neutral;
  const tone =
    neutral === 'access-denied' || neutral === 'not-found' || neutral === 'invalid'
      ? 'danger'
      : neutral === 'unreachable'
        ? 'warning'
        : null;
  if (!tone) return null;
  const carried = rows.some((r) => r.resourceId === app.resourceId && r.referenceStatus?.neutral === neutral);
  return carried ? null : tone;
}
